import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router-dom'

import { showPreviewNotice } from '../../lib/appSurfaces'
import { Container } from '../ui/Container'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE "OUTSIDE SCOPE" BAND ON THE WEBSITE'S APP-BACKED PAGES — 7 Oct 2026
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Docs/34-HIDDEN-SURFACES.md` § 1b is the full record. The short version:
 * nineteen funnels into the directory, the open requests and the deals explainer
 * were hidden on the morning of 7 Oct because none of them was asked for and all
 * of them are unpaid. The position changed the same evening — show them, label
 * them, let the client decide whether to pay — and this component is the label.
 *
 * 🔴 UPDATE, 12 OCT 2026: THE CLIENT SAID HE DOES NOT NEED THESE YET, SO THE
 * STATE IS `'hidden'` AND THIS BAND RENDERS NOWHERE. It is not dead code — it is
 * the middle of three states, and it is the one to use for a demo.
 *
 * ⚠️ THREE STATES, NOT TWO, AND ALL THREE ARE NOW ONE WORD IN
 * `src/lib/appSurfaces.ts`. This component no longer owns the decision:
 *
 *   'hidden'      the nineteen funnels are gone, so a band on six pages nobody
 *                 can reach from the site would be an orphan ← WHERE WE ARE
 *   'preview'     visible + this notice. **Flip to this before showing him the
 *                 work**: he sees exactly what the USD 200 buys, labelled.
 *   'delivered'   visible, no notice. Set it when he pays — at that point the
 *                 work is delivered and calling it a preview is simply wrong.
 *
 * ─── HOW TO CHANGE THE WORDING ─────────────────────────────────────────────
 *
 * 🟠 TWO KEYS, `previewNotice.label` AND `previewNotice.body`, IN
 * `src/locales/en.json` AND `src/locales/nb.json`. THE DEVELOPER OWNS THIS
 * TEXT — it is a commercial message to one reader, not product copy, so change
 * it there and nowhere else, and change BOTH languages (the client is Norwegian
 * and reads the `nb` build).
 *
 * ⚠️ AND ONE CLAIM IS DELIBERATELY NOT MADE. The first instinct was *"mock data,
 * backend under development"*. Half of that is true and half is checkable and
 * false: these pages have a complete backend — RLS, the `business_search_text`
 * computed column, the three trust states, the directory queries, 548 passing
 * smoke checks. **A claim that is checkable and wrong costs credibility on
 * everything said alongside it**, so the band says what IS true: the data is
 * demo data, and the section is outside the current scope of work.
 *
 * ─── WHY THE STRINGS ARE IN THE BASE CATALOGUE AND MUST STAY THERE ─────────
 *
 * 🔴 EVERY ROUTE BELOW IS LAZY, AND THEY DO NOT ALL REGISTER THE SAME CATALOGUE.
 * `/requests`, `/requests/:id` and `/deals` import `locales/pages/register`;
 * `/categories`, `/business/:slug` and `/:locale/:city/:category` do not — they
 * read the base catalogue only. A component rendered from both sides of that
 * split must take its strings from the EAGER base catalogue or it renders a raw
 * key on half of them. That exact failure shipped to the live site once
 * (`HOMESECTIONS.SHOT.PENDING`), which is why it is written down here.
 *
 * This component is mounted in `RootLayout`, which is eager, so the keys belong
 * in `locales/en.json` / `nb.json`. **Do not move them into `locales/pages/`.**
 *
 * ─── IT MUST NOT COST LAYOUT ───────────────────────────────────────────────
 *
 * The band is derived synchronously from `pathname` — no query, no await — so it
 * is present on first paint and nothing below it moves. It is deliberately two
 * lines of small type rather than a card: at phone width it costs ~64 px, which
 * leaves every one of these pages' headings above the fold.
 *
 * It is a NOTICE, NOT AN ERROR. `warning-subtle` is the amber pair from
 * `packages/config/src/tokens.ts` and is defined in both themes
 * (`#FFEFCD` on `#7C4500` in light, `#33260C` on `#F5C97A` in dark), so this
 * needs no `dark:` variant. `destructive` would read as a fault on a page that
 * is working correctly.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🟠 THE ONE LIST — BUT IT IS NO LONGER THE SWITCH. `appSurfaces.ts` IS.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ THIS BLOCK USED TO SAY "turning the notice off is emptying it". **Do not
 * do that any more** — emptying this array would turn the band off while
 * leaving the nineteen funnels in whatever state they are in, which is the
 * `'delivered'` state reached by accident. One word in `appSurfaces.ts` moves
 * the band and the funnels together, which is the whole point of that file.
 * Delete a line HERE only to drop the band from ONE page while keeping it on
 * the others.
 *
 * These are the six routes that render app data on the website, and they are the
 * same six `Docs/34` § 1a lists. ⚠️ `/blog` AND `/blog/:slug` ARE DELIBERATELY
 * NOT HERE: the developer kept the blog visible throughout the hiding because it
 * is ordinary content — real articles, written by businesses, under their own
 * byline — rather than a window into the app's data.
 *
 * They are `matchPath` patterns, not prefixes, so `/requests/:id` is matched as
 * one segment rather than by `startsWith`.
 */
const NOTICE_ROUTES = [
  '/categories',
  '/business/:slug',
  '/requests',
  '/requests/:id',
  '/deals',
  /*
   * ⚠️ THE DIRECTORY PATTERN IS THREE BARE SEGMENTS, SO IT MATCHES MORE THAN THE
   * DIRECTORY. It is last here for the same reason `App.tsx` declares it last,
   * and `EXCLUDED_ROUTES` below is the part that keeps it honest.
   */
  '/:locale/:city/:category',
] as const

/**
 * ⚠️ WITHOUT THIS, THE NOTICE APPEARS ON THE RETIRED DISCOVER PAGES.
 *
 * `/:locale/:city/:category` is three unconstrained segments, and the three
 * surviving Discover URLs (`/discover/q/:id`, `/discover/p/:slug`,
 * `/discover/topic/:slug`) are also three segments — so `matchPath` captures
 * them even though React Router gives the route itself to `RetiredDiscoverPage`,
 * which ranks static segments above dynamic ones. A page explaining that a
 * feature was retired does not also need a band calling it a preview.
 *
 * ⚠️ THESE ARE THE ONLY THREE-SEGMENT ROUTES IN `App.tsx` BESIDES THE DIRECTORY
 * — counted, not assumed. `/legal/privacy`, `/blog/:slug` and `/jobs/:id` are
 * two. **If a three-segment route is ever added, add it here in the same
 * commit**, or it silently inherits this band.
 */
const EXCLUDED_ROUTES = ['/discover/q/:id', '/discover/p/:slug', '/discover/topic/:slug'] as const

/*
 * Deliberately NOT exported. This file exports one component and nothing else,
 * which keeps `react-refresh/only-export-components` quiet and keeps the route
 * list in the same file as the band that reads it — the single edit Docs/34 § 1b
 * promises. If something else ever needs to ask the question, move the two
 * arrays and this function to `lib/` rather than exporting them from here.
 */
const pathCarriesPreviewNotice = (pathname: string): boolean => {
  /*
   * ⚠️ THE STATE CHECK IS FIRST, BEFORE EITHER PATTERN LIST. In `'hidden'` the
   * band must not render anywhere, and in `'delivered'` the work is paid for —
   * both are decided by the state, not by the path, so asking the path first
   * would be two answers to one question.
   */
  if (!showPreviewNotice()) return false
  if (EXCLUDED_ROUTES.some((pattern) => matchPath(pattern, pathname) !== null)) return false
  return NOTICE_ROUTES.some((pattern) => matchPath(pattern, pathname) !== null)
}

export const PreviewNotice = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  if (!pathCarriesPreviewNotice(pathname)) return null

  return (
    <div className="border-b border-warning-subtle-foreground/20 bg-warning-subtle text-warning-subtle-foreground">
      <Container as="div" className="flex items-start gap-3 py-3">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <p className="type-body-sm text-balance">
          <strong className="font-semibold">{t('previewNotice.label')}</strong>{' '}
          {t('previewNotice.body')}
        </p>
      </Container>
    </div>
  )
}
